/**
 * Submission Tracker Utility
 * Tracks form submissions to detect spam/abuse and enable CAPTCHA when needed
 */

const STORAGE_PREFIX = 'form_submission_';
const TIME_WINDOW = 5 * 60 * 1000; // 5 minutes in milliseconds
const MAX_ATTEMPTS_WITHOUT_CAPTCHA = 2; // After 2 attempts, require CAPTCHA
const COOLDOWN_PERIOD = 15 * 60 * 1000; // 15 minutes cooldown after suspicious activity

/**
 * Get submission history for a specific form
 * @param {string} formName - Name of the form (e.g., 'contact', 'newsletter', 'popup')
 * @returns {Array} Array of timestamp objects
 */
export const getSubmissionHistory = (formName) => {
    try {
        const stored = localStorage.getItem(`${STORAGE_PREFIX}${formName}`);
        if (!stored) return [];
        
        const history = JSON.parse(stored);
        const now = Date.now();
        
        // Filter out old attempts outside the time window
        const recentHistory = history.filter(
            attempt => (now - attempt.timestamp) < TIME_WINDOW
        );
        
        // Update storage with filtered history
        if (recentHistory.length !== history.length) {
            localStorage.setItem(
                `${STORAGE_PREFIX}${formName}`,
                JSON.stringify(recentHistory)
            );
        }
        
        return recentHistory;
    } catch (error) {
        console.error('Error reading submission history:', error);
        return [];
    }
};

/**
 * Record a submission attempt
 * @param {string} formName - Name of the form
 * @returns {Object} Updated history
 */
export const recordSubmissionAttempt = (formName) => {
    try {
        const history = getSubmissionHistory(formName);
        const newAttempt = {
            timestamp: Date.now(),
            id: Math.random().toString(36).substr(2, 9)
        };
        
        history.push(newAttempt);
        
        localStorage.setItem(
            `${STORAGE_PREFIX}${formName}`,
            JSON.stringify(history)
        );
        
        return history;
    } catch (error) {
        console.error('Error recording submission:', error);
        return [];
    }
};

/**
 * Check if CAPTCHA should be required based on submission history
 * @param {string} formName - Name of the form
 * @returns {boolean} True if CAPTCHA should be required
 */
export const shouldRequireCaptcha = (formName) => {
    const history = getSubmissionHistory(formName);
    
    // Check if there are too many attempts in the time window
    if (history.length >= MAX_ATTEMPTS_WITHOUT_CAPTCHA) {
        return true;
    }
    
    // Check if user is in cooldown period
    const cooldownKey = `${STORAGE_PREFIX}${formName}_cooldown`;
    const cooldownUntil = localStorage.getItem(cooldownKey);
    
    if (cooldownUntil) {
        const cooldownTime = parseInt(cooldownUntil, 10);
        if (Date.now() < cooldownTime) {
            return true;
        } else {
            // Cooldown expired, remove it
            localStorage.removeItem(cooldownKey);
        }
    }
    
    return false;
};

/**
 * Set cooldown period after suspicious activity
 * @param {string} formName - Name of the form
 */
export const setCooldown = (formName) => {
    const cooldownKey = `${STORAGE_PREFIX}${formName}_cooldown`;
    const cooldownUntil = Date.now() + COOLDOWN_PERIOD;
    localStorage.setItem(cooldownKey, cooldownUntil.toString());
};

/**
 * Clear submission history for a form (use after successful submission with CAPTCHA)
 * @param {string} formName - Name of the form
 */
export const clearSubmissionHistory = (formName) => {
    try {
        localStorage.removeItem(`${STORAGE_PREFIX}${formName}`);
        localStorage.removeItem(`${STORAGE_PREFIX}${formName}_cooldown`);
    } catch (error) {
        console.error('Error clearing submission history:', error);
    }
};

/**
 * Get time remaining in cooldown (in minutes)
 * @param {string} formName - Name of the form
 * @returns {number} Minutes remaining, or 0 if no cooldown
 */
export const getCooldownTimeRemaining = (formName) => {
    const cooldownKey = `${STORAGE_PREFIX}${formName}_cooldown`;
    const cooldownUntil = localStorage.getItem(cooldownKey);
    
    if (!cooldownUntil) return 0;
    
    const timeRemaining = parseInt(cooldownUntil, 10) - Date.now();
    if (timeRemaining <= 0) {
        localStorage.removeItem(cooldownKey);
        return 0;
    }
    
    return Math.ceil(timeRemaining / 60000); // Convert to minutes
};

/**
 * Check for suspicious patterns in submission attempts
 * @param {string} formName - Name of the form
 * @returns {boolean} True if suspicious activity detected
 */
export const detectSuspiciousActivity = (formName) => {
    const history = getSubmissionHistory(formName);
    
    if (history.length < 3) return false;
    
    // Check if submissions are too rapid (less than 30 seconds apart)
    for (let i = 1; i < history.length; i++) {
        const timeDiff = history[i].timestamp - history[i - 1].timestamp;
        if (timeDiff < 30000) { // 30 seconds
            return true;
        }
    }
    
    // Check if there are more than 5 attempts in the time window
    if (history.length > 5) {
        return true;
    }
    
    return false;
};

/**
 * Get submission statistics for a form
 * @param {string} formName - Name of the form
 * @returns {Object} Statistics object
 */
export const getSubmissionStats = (formName) => {
    const history = getSubmissionHistory(formName);
    const cooldownMinutes = getCooldownTimeRemaining(formName);
    const requiresCaptcha = shouldRequireCaptcha(formName);
    const suspicious = detectSuspiciousActivity(formName);
    
    return {
        attemptCount: history.length,
        requiresCaptcha,
        inCooldown: cooldownMinutes > 0,
        cooldownMinutes,
        suspicious,
        canSubmit: !suspicious || requiresCaptcha
    };
};

